const REDACTED_VALUE = "[REDACTED]";

export const SENSITIVE_TELEMETRY_KEYS = [
  "authorization",
  "cookie",
  "set-cookie",
  "code",
  "state",
  "nonce",
  "session_state",
  "access_token",
  "refresh_token",
  "id_token",
  "token",
  "ticket",
  "error_description",
  "crmtoken",
  "crm_token",
  "password",
  "passwd",
  "mfa",
  "otp",
];

const SENSITIVE_KEY_SET = new Set(
  SENSITIVE_TELEMETRY_KEYS.map((key) => key.replace(/[^a-z0-9]/gi, ""))
);

function isSensitiveKey(key) {
  return SENSITIVE_KEY_SET.has(
    String(key).toLowerCase().replace(/[^a-z0-9]/g, "")
  );
}

export function sanitizeTelemetryUrl(value) {
  if (typeof value !== "string" || !value) return value;

  const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(value);
  const looksLikeUrl =
    isAbsolute ||
    value.startsWith("/") ||
    value.startsWith("?") ||
    value.includes("/auth/sso/callback");
  if (!looksLikeUrl) return value;

  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://local.invalid";
    const url = new URL(value, base);
    const isCallback = url.pathname === "/auth/sso/callback";

    for (const key of Array.from(url.searchParams.keys())) {
      if (isCallback || isSensitiveKey(key)) {
        url.searchParams.delete(key);
      }
    }
    url.hash = "";

    return isAbsolute ? url.toString() : url.pathname + url.search;
  } catch (error) {
    if (value.includes("/auth/sso/callback")) {
      return value.split("?")[0].split("#")[0];
    }
    return value;
  }
}

export function redactTelemetryData(value, options = {}, seen = new WeakSet()) {
  const depth = options.depth || 0;
  if (depth > 10) return "[TRUNCATED]";
  if (value == null || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("{") || trimmed.startsWith("[")) &&
      trimmed.length < 100000
    ) {
      try {
        return redactTelemetryData(
          JSON.parse(trimmed),
          { depth: depth + 1 },
          seen
        );
      } catch (error) {
        // Non-JSON strings continue through URL sanitization.
      }
    }
    return sanitizeTelemetryUrl(value);
  }
  if (typeof value === "function") return "[FUNCTION]";
  if (typeof value !== "object") return String(value);
  if (seen.has(value)) return "[CIRCULAR]";
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) =>
      redactTelemetryData(item, { depth: depth + 1 }, seen)
    );
  }

  const redacted = {};
  for (const [key, item] of Object.entries(value)) {
    redacted[key] = isSensitiveKey(key)
      ? REDACTED_VALUE
      : redactTelemetryData(item, { depth: depth + 1 }, seen);
  }
  return redacted;
}

export function redactBugsnagEvent(event) {
  try {
    if (event?.request?.url) {
      event.request.url = sanitizeTelemetryUrl(event.request.url);
    }
    if (event?.context) {
      event.context = sanitizeTelemetryUrl(event.context);
    }
    if (Array.isArray(event?.breadcrumbs)) {
      event.breadcrumbs.forEach((breadcrumb) => {
        breadcrumb.metadata = redactTelemetryData(breadcrumb.metadata || {});
      });
    }

    if (typeof event?.getMetadata === "function") {
      const metadata = event.getMetadata();
      for (const [section, sectionData] of Object.entries(metadata || {})) {
        if (typeof event.clearMetadata === "function") {
          event.clearMetadata(section);
        }
        event.addMetadata(section, redactTelemetryData(sectionData));
      }
    }
  } catch (error) {
    // Reporting must never fail because a vendor event shape changed.
  }
  return true;
}

export { REDACTED_VALUE };
